import { authFetch } from '../utils/authFetch';
import {
  User,
  UserCreateRequest,
  UserUpdateRequest,
  UserSearchRequest,
  UserListResponse
} from '../types/organizations';

const API_BASE = (process.env.REACT_APP_BACKEND_BASE_URL || 'http://localhost:8000') + '/api/v1';

export const userService = {
  // Get paginated list of users
  async getUsers(
    params: UserSearchRequest = {},
    accessToken: string | null,
    setUserAndToken: (user: any, token: string) => void
  ): Promise<UserListResponse> {
    const searchParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          value.forEach(v => searchParams.append(key, v));
        } else {
          searchParams.append(key, String(value));
        }
      }
    });

    const response = await authFetch(`${API_BASE}/users?${searchParams.toString()}`, {}, accessToken, setUserAndToken);
    if (!response.ok) {
      throw new Error('Failed to fetch users');
    }
    return await response.json();
  },

  // Get user by ID
  async getUser(
    id: string,
    accessToken: string | null,
    setUserAndToken: (user: any, token: string) => void
  ): Promise<User> {
    const response = await authFetch(`${API_BASE}/users/${id}`, {}, accessToken, setUserAndToken);
    if (!response.ok) {
      throw new Error('Failed to fetch user');
    }
    const data = await response.json();
    return data.data;
  },

  // Create new user
  async createUser(
    user: UserCreateRequest,
    accessToken: string | null,
    setUserAndToken: (user: any, token: string) => void
  ): Promise<User> {
    const response = await authFetch(`${API_BASE}/users`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(user),
    }, accessToken, setUserAndToken);
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to create user');
    }
    const data = await response.json();
    return data.data;
  },

  // Update user
  async updateUser(
    id: string,
    user: UserUpdateRequest,
    accessToken: string | null,
    setUserAndToken: (user: any, token: string) => void
  ): Promise<User> {
    const response = await authFetch(`${API_BASE}/users/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(user),
    }, accessToken, setUserAndToken);
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to update user');
    }
    const data = await response.json();
    return data.data;
  },

  // Delete user (soft delete)
  async deleteUser(
    id: string,
    accessToken: string | null,
    setUserAndToken: (user: any, token: string) => void
  ): Promise<void> {
    const response = await authFetch(`${API_BASE}/users/${id}`, {
      method: 'DELETE',
    }, accessToken, setUserAndToken);
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to delete user');
    }
  },

  // Search users with advanced filters
  async searchUsers(
    searchParams: UserSearchRequest,
    accessToken: string | null,
    setUserAndToken: (user: any, token: string) => void
  ): Promise<UserListResponse> {
    const response = await authFetch(`${API_BASE}/users/search`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(searchParams),
    }, accessToken, setUserAndToken);
    
    if (!response.ok) {
      throw new Error('Failed to search users');
    }
    return await response.json();
  },

  // Get users for a specific organization
  async getOrganizationUsers(
    organizationId: string,
    page: number = 1,
    pageSize: number = 20,
    accessToken: string | null,
    setUserAndToken: (user: any, token: string) => void
  ): Promise<UserListResponse> {
    const searchParams = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString()
    });

    const response = await authFetch(`${API_BASE}/organizations/${organizationId}/users?${searchParams.toString()}`, {}, accessToken, setUserAndToken);
    if (!response.ok) {
      throw new Error('Failed to fetch organization users');
    }
    return await response.json();
  },

  // Set user as primary contact for organization
  async setPrimaryContact(
    organizationId: string,
    userId: string,
    accessToken: string | null,
    setUserAndToken: (user: any, token: string) => void
  ): Promise<void> {
    const response = await authFetch(`${API_BASE}/organizations/${organizationId}/users/${userId}/set-primary-contact`, {
      method: 'POST',
    }, accessToken, setUserAndToken);
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to set primary contact');
    }
  },
};
